<div class="heading skin-detail-heading" (resize)="handleResize($event)">
  @if (canGoBack()) {
    <app-button2 appearance="secondary-ghost" size="s" ariaLabel="Go back" (click)="handleGoBack()">
      <app-icon name="chevron-left" />
    </app-button2>
  }

  <app-input
    class="input"
    appearance="sapphire-inline"
    [inputFormControl]="form.controls.title"
    placeholder="New File"
    [errorPlaceholder]="'Add title'"
    [withErrors]="submitted"
    size="s"
    [customStyles]="contracted ? { 'text-overflow': 'ellipsis' } : undefined"
    [attr.title]="contracted ? form.controls.title.value : null"
  />

  <div class="icons">
    <div [appPreventDirtyClick]="dirtyFormRef" class="contents">
      <!-- [MUST-002] 'Bug' debug quick-action hidden per task; uncomment to re-enable
      <stch-quick-action
        *ngIf="!contracted"
        appearance="sapphire-toggle"
        size="m"
        icon="bug"
        [active]="selectedTab === 'debug'"
        matTooltip="Debug"
        (onClick)="selectedTab = 'debug'"
      />
      -->

      <stch-quick-action
        *ngIf="!contracted"
        appearance="sapphire-toggle"
        size="m"
        icon="sapphire-archive2"
        [active]="form.controls.archived.value"
        [matTooltip]="form.controls.archived.value ? Labels.RestoreFromArchive : Labels.Archive"
        [disabled]="!file?.id"
        (onClick)="archive()"
      />

      <button
        data-file-trash-action
        *ngIf="!contracted"
        type="button"
        class="inline-flex border-0 bg-transparent p-0"
        [attr.aria-label]="form.controls.deleted.value ? Labels.DeletePermanently : Labels.Trash"
        [disabled]="!file?.id || lifecycleState() !== 'ready'"
        (click)="trashButtonClick($event)"
      >
        <stch-trash-action
          [disabled]="!file?.id || lifecycleState() !== 'ready'"
          [deleted]="form.controls.deleted.value"
          (onClick)="delete()"
        />
      </button>
      @if (lifecycleState() !== 'ready') {
        <span role="status">{{ lifecycleState() === 'reading' ? 'Checking file status…' : 'File status could not be checked. Deletion is unavailable.' }}</span>
      }

      <ng-container *ngComponentOutlet="workspaceComponents['app-item-workspace-chip']; inputs: workspaceChipInputs" />
      <stch-quick-action
        *ngIf="!contracted"
        appearance="sapphire-toggle"
        size="m"
        icon="sapphire-share"
        matTooltip="Share"
        [disabled]="!file?.id"
        [stchShareContextMenu]="file"
        [stchShareContextMenuDisabled]="!file?.id"
      />

      <app-button
        appearance="sapphire-secondary-ghost"
        size="xs"
        icon="sapphire-ellipsis"
        matTooltip="More options"
        ariaLabel="More options"
        [disabled]="!file?.id"
        (appFileContextMenuTab)="$event === 'access' ? openWorkspaceAccess() : openItemComments()"
        [appFileContextMenuElementLocation]="UILocations.mainView"
        [appFileContextMenu]="file"
        [appFileContextMenuDisabled]="!file?.id"
        appFileContextMenuMode="popover"
      />
    </div>

    @if (showCloseButton) {
      <app-button appearance="sapphire-secondary-ghost" size="xs" icon="sapphire-close" (click)="handleClose()" />
    }
  </div>
</div>

<!-- Badge shares the timestamps line, right-aligned, so the header shows where the item lives.
     Only `pr-8` here: app-stitch-timestamps brings its own `px-8`, and this matches the right padding
     the title row above uses, so the badge lines up under the header icons. It hangs 2px under them
     (`self-start -mt-[2px]`), as the prototype's badge column does, rather than centring on the dates. -->
<div class="flex items-center justify-between gap-2 pr-8">
  <app-stitch-timestamps
    class="block"
    createdLabel="Uploaded"
    [spaced]="true"
    [createdAt]="file?.createdAt"
    [updatedAt]="file?.updatedAt"
  />
  <ng-container *ngComponentOutlet="workspaceComponents['app-item-team-label']; inputs: workspaceLabelInputs" />
  <!-- stitch-app-shell FolderBadge: the file's folder in its colour. -->
  @if (headerFolder(); as container) {
    <app-container-badge
      type="file-folder"
      class="min-w-0 self-start -mt-[2px]"
      [title]="container.title"
      [color]="container.color"
    />
  } @else if (locationBadge(); as badge) {
    <app-search-location-badge class="min-w-0 self-start -mt-[2px]" [type]="badge.type" [name]="badge.name ?? ''" />
  }
</div>

<div class="tabs skin-detail-tabs">
  <app-tabs #workspaceTabControl appearance="sapphire" [values]="workspaceTabs" [value]="selectedTab" (valueChange)="selectWorkspaceTab($event)" [overflow]="tabsOverflow()" size="m" />

  <div class="filler"></div>

  @if (handoverTenantId()) {
    <app-button2
      appearance="secondary"
      size="m"
      [height]="32"
      rounding="lg"
      data-handover-anchor
      [ariaLabel]="handoverControl.accessibleName()"
      [ariaHaspopup]="'dialog'"
      [disabled]="!canAssignHandover() || handoverControl.busy()"
      [attr.title]="canAssignHandover() ? null : 'Requires edit access'"
      [ariaDescribedBy]="!canAssignHandover() ? handoverControl.ids.title + '-edit' : handoverControl.ids.busy"
      (click)="canAssignHandover() && handoverControl.openAssign()"
    >
      <app-icon name="sapphire-user-plus" size="m" />
      {{ handoverControl.buttonLabel() }}
      <app-icon name="sapphire-chevron-down" size="s" />
      <span class="sr-only" [id]="handoverControl.ids.title + '-edit'">{{ canAssignHandover() ? '' : 'Requires edit access' }}</span>
    </app-button2>
  }
  <stch-knowledge-button [active]="displayKnowledge" (click)="showKnowledge()" />
</div>
<app-item-handover
  #handoverControl
  [api]="handoverApi"
  [enabled]="!!handoverTenantId()"
  [showButton]="false"
  [itemId]="file?.id"
  [tenantId]="handoverTenantId()"
  [insideModal]="handoverInsideModal()"
  [stitchType]="StitchType.FILE"
  [prototypeWindow]="true"
  noun="file"
/>
<app-item-review
  [api]="reviewApi"
  [enabled]="!!handoverTenantId()"
  [itemId]="file?.id"
  [tenantId]="handoverTenantId()"
  [insideModal]="handoverInsideModal()"
  [stitchType]="StitchType.FILE"
  [contentVersion]="reviewContentVersion()"
/>

<div
  class="form"
  appDroppable
  appDroppableHoverClass="drag-over"
  [appDroppableDisabled]="!stitchItem?.id"
  [appDroppablePredicate]="dndPredicate"
  (appDroppableDrop)="dndDrop($event)"
  [appDroppableZIndex]="0"
>
  <div class="drop-area-border"></div>

  @if (selectedTab === 'stitch' && hasKnowledgeHubAccess()) {
    <app-entity-hub-search [contactId]="file?.id" />
  }

  <div class="form-heading">
    <div class="form-heading-nav">
      <div class="form-heading-nav-separator"></div>

    </div>

    <div *ngIf="displayKnowledge" class="form-heading-nav-underline"></div>

    <!-- One Knowledge box, rows in the skin's order: Knots · Tags · Connections. -->
    <div class="form-knowledge" [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())">
      <app-knots
        class="form-knots"
        [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
        [stitchItems]="file | toArray"
        [control]="form.controls['knots']"
        [stateKey]="StateKey.knotsList"
        [withControls]="selectedTab !== 'stitch'"
        [isHubMode]="selectedTab === 'stitch' && hasKnowledgeHubAccess()"
        [hubEntityUid]="file?.id"
      />

      <app-tags
        class="form-tags"
        [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
        [stitchItems]="[file]"
        [control]="form.controls['tags']"
        [stateKey]="StateKey.tagsList"
        [withControls]="selectedTab !== 'stitch'"
        [isHubMode]="selectedTab === 'stitch' && hasKnowledgeHubAccess()"
        [hubEntityUid]="file?.id"
      />

      <app-connections
        class="form-knots"
        [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
        [stitchItems]="[file]"
        [control]="form.controls['connections']"
        [stateKey]="StateKey.connectionsList"
        [withControls]="selectedTab !== 'stitch'"
        [isHubMode]="selectedTab === 'stitch' && hasKnowledgeHubAccess()"
        [hubEntityUid]="file?.id"
      />
    </div>
  </div>

  <app-scroll
    *ngIf="selectedTab !== 'file' && (selectedTab !== 'stitch' || (permissionsLoaded() && !hasKnowledgeHubAccess()))"
    [ngSwitch]="selectedTab"
    class="form-scroll"
    (top)="displayScrollShadow = !$event"
  >
    <div *ngIf="displayScrollShadow" class="form-scroll-shadow"></div>

    <div *ngSwitchCase="'details'" class="form-kit" [class.contracted]="contracted">
      <section class="form-kit-section">
        <div class="form-kit-field">
          <span class="form-kit-label">Folder</span>
          <app-autocomplete-input
            appearance="sapphire-outline"
            placeholder="Folder"
            [suggestions]="foldersSuggestions"
            [inputFormControl]="form.controls.folderId"
            size="s"
            [withErrors]="submitted"
            [withClear]="true"
          />
        </div>
      </section>

      <!-- stitch-app-shell InfoList: what the file is, read-only. -->
      <section class="form-kit-section">
        <h3 class="form-kit-section-title">Info</h3>
        <dl class="form-kit-info">
          <div class="form-kit-info-row">
            <dt>Size</dt>
            <dd>
              @if (file?.attachmentSize) {
                {{ file.attachmentSize | fileSize }}
              } @else {
                —
              }
            </dd>
          </div>
        </dl>
      </section>

      <section class="form-kit-section">
        <stch-item-description-field
          [control]="form.controls.description"
          [item]="file"
          [tagsControl]="form.controls['tags']"
          [knotsControl]="form.controls['knots']"
        />
      </section>

      <!-- Team workspace handover (main): who answers for the file and who made it, as a read-only InfoList. -->
      @if (handoverTenantId()) {
        <section class="form-kit-section">
          <dl class="form-kit-info">
            <div class="form-kit-info-row">
              <dt>Responsible</dt>
              <dd data-testid="details-responsible">{{ handoverControl.responsibleName() }}</dd>
            </div>
            @if (handoverControl.creatorName(); as author) {
              <div class="form-kit-info-row">
                <dt>Created by</dt>
                <dd data-testid="details-authored-by">{{ author }}</dd>
              </div>
            }
          </dl>
        </section>
      }
    </div>

    <app-context-menu-state-indicator
      *ngSwitchCase="'stitch'"
      class="block ml-auto w-fit mt-5 mr-4"
      svgIcon="filters"
      appStitchListContextMenu
      (appStitchListContextMenuChange)="stitchListState = $event"
    />

    <app-linked-info-list
      *ngSwitchCase="'stitch'"
      class="form-linked-info"
      [stitch]="file"
      [state]="stitchListState"
      (clickStitchItem)="handleClickItem($event)"
      (dblClickStitchItem)="handleDblClickItem($event)"
    />

    <div *ngSwitchCase="'access'" class="form-linked-info">
      <ng-container
        *ngComponentOutlet="workspaceComponents['app-item-workspace-access']; inputs: workspaceAccessInputs"
      />
    </div>

    <stch-comments (countChanged)="commentsCount.set($event)" [insideTeam]="insideTeam" *ngSwitchCase="'comments'" class="form-linked-info" [stitch]="file" />

    <ng-container *ngSwitchCase="'versions'">
      @if (historyAvailable()) {
        <ng-container *ngComponentOutlet="workspaceComponents['app-file-version-history']; inputs: versionHistoryInputs" />
      }
    </ng-container>

    <ng-container *ngSwitchCase="'activity'">
      @if (!footerActivityPresentation()) {
        @if (itemChangesInputs; as inputs) {
          <ng-container *ngComponentOutlet="workspaceComponents['app-item-changes']; inputs: inputs" />
        }
      }
    </ng-container>

    <stch-json-viewer *ngSwitchCase="'debug'" [json]="file?.debug" />

    <ng-container *ngSwitchCase="'hub'">
      @if (!permissionsLoaded()) {
        <!-- Render nothing until permission rules arrive to avoid flashing the wrong pane -->
      } @else if (hasKnowledgeHubAccess()) {
        <app-knowledge-hub [item]="file" />
      } @else {
        <app-feature-upsell
          title="Knowledge Hub"
          description="AI-powered summaries, entities, themes, and key terms for your items. Upgrade your plan to unlock Knowledge Hub."
          imageSrc="assets/placeholders/placeholder-files.svg"
        />
      }
    </ng-container>
  </app-scroll>

  @if (selectedTab === 'stitch' && hasKnowledgeHubAccess()) {
    <app-stitch-hub
      [stitch]="file"
      (clickStitchItem)="handleClickItem($event)"
      (dblClickStitchItem)="handleDblClickItem($event)"
    />
  }

  <stch-save-cancel-panel
    [enabled]="!pristine"
    [uploading]="isUploading()"
    [saveLabel]="saveLabel()"
    (onCancel)="handleCancel()"
    (onSave)="handleSubmit()"
  />

  @if (shouldRenderPreview) {
    <app-file-preview class="form-preview" [class.hidden]="selectedTab !== 'file'" [file]="previewFile ?? file" />
  }
  @if (hasFileLink(file) && selectedTab === 'file') {
    <div class="form-preview p-8 break-all">
      @if (!file.id) {
        <app-input [inputFormControl]="form.controls.link" placeholder="Web address (http or https)" [withErrors]="submitted"
          [validationMessages]="{ fileLink: 'Enter a valid http or https web address, up to 2048 characters, without spaces.' }" />
      } @else if (isValidFileLink(file.link)) {
        <a [href]="file.link" target="_blank" rel="noopener" aria-label="Open file link in a new tab">{{ file.link }}</a>
      } @else {
        <p>{{ file.link }}</p><p>This file link cannot be opened.</p>
      }
    </div>
  }

  @if (!file?.id && workspaceIdentity(); as identity) {
    <app-tenancy-selector
      [spaceTitle]="identity.spaceTitle"
      [spaceTenantId]="identity.spaceTenantId"
      [memberTenantId]="identity.memberTenantId"
      itemNoun="file"
    [locked]="!!file?.accountId || !!form.controls.attachment.value"
    [lockedReason]="file?.accountId ? 'This draft already belongs to a workspace.' : null"
      [tenantId]="sharingTenantId()"
      (tenantIdChange)="pickDestination($event)"
    />
  }

  @if (file.pdfSource && !file.id) {
    <p role="status" class="px-8 py-4">The PDF will be generated from the dropped item after Save.</p>
  }

  <app-files-input
    *ngIf="selectedTab === 'file' && !file?.id && !file.pdfSource && !hasFileLink(file)"
    class="form-file-input"
    [titleFormControl]="form.controls.title"
    [inputFormControl]="form.controls.attachment"
    [withErrors]="submitted"
    [validationMessages]="validationMessages()"
    [tenantItems]="tenantCarrier()"
    (uploading)="onUploadingChange($event)"
  />

  @if (selectedTab === 'file' && canReplace()) {
    <section data-file-replace class="flex flex-col gap-4 px-8 py-4" aria-label="File version">
      <div class="flex items-center gap-8">
        <p class="min-w-0 flex-1 truncate text-base">{{ file.attachmentName }} · {{ file.attachmentSize | fileSize }}</p>
        @if (replaceOpen()) {
          <button type="button" class="rounded border border-border px-8 py-4" (click)="cancelReplace()">Cancel</button>
        } @else {
          <button type="button" class="rounded border border-border px-8 py-4" (click)="openReplace()">Replace file</button>
        }
      </div>
      @if (replaceOpen()) {
        <div role="group" [attr.aria-labelledby]="replaceLabelId">
          <p class="text-base font-medium" [id]="replaceLabelId">Browse Files to replace this version</p>
          <app-files-input
            [inputFormControl]="replaceUpload"
            [titleFormControl]="replaceTitle"
            [tenantItems]="replaceOwner"
            (uploading)="onReplaceUploading($event)"
          />
        </div>
      }
      @if (replaceError(); as reason) {
        <p role="alert" class="text-base">{{ reason }}</p>
      }
    </section>
  }

</div>

<ng-container *ngComponentOutlet="workspaceComponents['app-item-quick-comment']; inputs: quickCommentInputs" />

<!-- stitch-app-shell breadcrumbs footer in the main view, as in every app; elsewhere the workspace footer. -->
@if (navigation.enabled && file?.id) {
  <app-detail-breadcrumbs
    [crumbs]="breadcrumbs()"
    [status]="form.controls.archived.value ? 'Archived' : form.controls.deleted.value ? 'Trash' : undefined"
  />
} @else {
  <ng-container *ngComponentOutlet="workspaceComponents['app-item-workspace-footer']; inputs: workspaceFooterInputs" />
}
